import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefillEmail = new URLSearchParams(location.search).get('email') ?? '';
  const [email, setEmail] = useState(prefillEmail);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    const { error: authError } = await supabase.auth.resetPasswordForEmail(
      email.trim(),
      { redirectTo: `${window.location.origin}${import.meta.env.BASE_URL}reset-password` }
    );
    setLoading(false);
    if (authError) {
      setError(authError.message);
      return;
    }
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-sidebar flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <img
            src="https://public.readdy.ai/ai/img_res/d3c3ec87-e7de-4a4f-b250-82f788f182b3.png"
            alt="API MART"
            className="w-14 h-14 rounded-xl mx-auto mb-4 object-cover"
          />
          <h1 className="text-2xl font-bold text-white">API MART</h1>
          <p className="text-white/40 text-sm mt-1">PASSWORD RECOVERY</p>
        </div>

        <div className="bg-white rounded-2xl p-6">
          {!sent ? (
            <>
              <div className="mb-5">
                <div className="w-12 h-12 bg-brand-50 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-mail-send-line text-brand-500 text-2xl" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Forgot your password?</h2>
                <p className="text-xs text-gray-400 mt-1">
                  Enter your account email and we&apos;ll send you a reset link.
                </p>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5"
                >
                  <i className="ri-error-warning-fill text-red-500" />
                  {error}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Email Address</label>
                  <input
                    type="email"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder="you@example.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-medium rounded-lg text-sm cursor-pointer whitespace-nowrap transition-colors"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />Sending...
                    </span>
                  ) : 'Send Reset Link'}
                </button>
              </form>

              <button
                onClick={() => navigate('/login')}
                className="w-full mt-3 text-center text-xs text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-line mr-1" />Back to Sign In
              </button>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-4"
            >
              <div className="w-14 h-14 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-green-500 text-3xl" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Check your inbox</h3>
              <p className="text-xs text-gray-500 leading-relaxed mb-5">
                We&apos;ve sent a password reset link to <span className="font-medium text-gray-700">{email}</span>.
                It may take a minute to arrive.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full py-2.5 bg-brand-500 hover:bg-brand-600 text-white font-medium rounded-lg text-sm cursor-pointer whitespace-nowrap transition-colors"
              >
                Back to Sign In
              </button>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
